import React from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import { Info as InfoIcon } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { EMPTY_OBJECT } from '~/src/Lib/Utils'

const displayName = 'Widget/Base'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    gridTemplateAreas: [
      '"icon label"',
      '"icon title"',
      '"icon description"',
    ].join('\n'),
    gridTemplateColumns: 'auto 1fr',
    gridTemplateRows: 'auto 1fr auto',
    gridColumnGap: theme.spacing(),
    '& $description:empty, & $label:empty': {
      height: 0,
    },
    color: theme.palette.text.primary,
  },
  title: {
    gridArea: 'title',
    alignSelf: 'center',
    color: 'inherit'
  },
  description: {
    gridArea: 'description',
  },
  label: {
    gridArea: 'label',
  },
  icon: {
    gridArea: 'icon',
    display: 'flex',
    width: 32,
    height: 32,
    placeItems: 'start center',
    placeContent: 'start center',
    '& [class*=MuiSvgIcon-root]': {
      marginTop: theme.spacing(0.5),
    },
    '& [class*=MuiAvatar-root]': {
      padding: 0,
      width: 32,
      height: 32,
      fontSize: '1rem',
    }
  },
}), { name: displayName })

export const WidgetBase = ({
  className,
  description = '',
  descriptionProps = EMPTY_OBJECT,
  href = undefined,
  id = undefined,
  Icon = InfoIcon,
  label = '',
  labelProps = EMPTY_OBJECT,
  title = '',
  titleProps = EMPTY_OBJECT,
  ...props
}) => {
  const Container = href ? 'a' : 'div'
  const classes = useStyles(props)
  const labelClassName = classNames(
    classes.label,
    label && label.props?.className,
    labelProps?.className
  )
  const titleClassName = classNames(
    classes.title,
    title && title.props?.className,
    titleProps?.className
  )
  const descriptionClassName = classNames(
    classes.description,
    description && description.props?.className,
    descriptionProps?.className
  )

  return (
    <Container href={href} className={classNames(classes.root, className)}>
      {React.isValidElement(label) ? React.cloneElement(label, {
        ...labelProps,
        ...label.props,
        className: labelClassName
      }) : (
        <Typography variant="body2" {...labelProps} className={labelClassName}>
          {label}
        </Typography>
      )}
      {React.isValidElement(title) ? React.cloneElement(title, {
        ...title.props,
        ...titleProps,
        id,
        className: titleClassName
      }) : (
        <Typography variant="subtitle1" {...titleProps} className={titleClassName} id={id}>
          {title}
        </Typography>
      )}
      {React.isValidElement(description) ? React.cloneElement(description, {
        ...description.props,
        ...descriptionProps,
        className: descriptionClassName,
      }) : (
        <Typography variant="body1" {...descriptionProps} className={descriptionClassName}>
          {description}
        </Typography>
      )}

      <div className={classes.icon}>
        {React.isValidElement(Icon) ? Icon : <Icon color="primary" />}
      </div>
    </Container>
  )
}
WidgetBase.propTypes = {
  description: PropTypes.node,
  href: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.node,
  title: PropTypes.node.isRequired,
  Icon: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.elementType,
  ]),
  descriptionProps: PropTypes.shape({
    className: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
    gutterBottom: PropTypes.bool,
    noWrap: PropTypes.bool,
    paragraph: PropTypes.bool,
    display: PropTypes.string,
    align: PropTypes.string,
  }),
  labelProps: PropTypes.shape({
    className: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
    gutterBottom: PropTypes.bool,
    noWrap: PropTypes.bool,
    paragraph: PropTypes.bool,
    display: PropTypes.string,
    align: PropTypes.string,
  }),
  titleProps: PropTypes.shape({
    className: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
    gutterBottom: PropTypes.bool,
    noWrap: PropTypes.bool,
    paragraph: PropTypes.bool,
    display: PropTypes.string,
    align: PropTypes.string,
  }),
}

export default WidgetBase
